import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from '@mui/material/TablePagination';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TodayIcon from '@mui/icons-material/Today';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { StatCard } from '@/features/admin/components/StatCard';
import {
  useAdminBookingStats,
  useAdminBookings,
} from '@/features/admin-bookings/hooks/useAdminBookings';
import { AdminBookingsTable } from '@/features/admin-bookings/components/AdminBookingsTable';
import { AdminBookingDetailDrawer } from '@/features/admin-bookings/components/AdminBookingDetailDrawer';
import { ADMIN_BOOKING_TABS, type AdminBookingTabKey } from '@/features/admin-bookings/constants';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { BookingStatus } from '@/types';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const SEARCH_DEBOUNCE_MS = 350;

function getTab(value: string | null) {
  return ADMIN_BOOKING_TABS.find((tab) => tab.key === value) ?? ADMIN_BOOKING_TABS[0];
}

/**
 * Every filter lives in the URL so notification links (`?id=`) and shared links
 * open exactly the same view.
 */
export function AdminBookingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = getTab(searchParams.get('tab'));
  const q = searchParams.get('q') ?? '';
  const dateField = searchParams.get('dateField') === 'travelDate' ? 'travelDate' : 'createdAt';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const page = Math.max(0, Number(searchParams.get('page') ?? 0) || 0);
  const limit = ROWS_PER_PAGE_OPTIONS.includes(Number(searchParams.get('limit')))
    ? Number(searchParams.get('limit'))
    : 20;
  const selectedId = searchParams.get('id');

  const [searchInput, setSearchInput] = useState(q);

  const updateParams = (changes: Record<string, string | null>, resetPage = true) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  // Keep the box in sync when the URL changes from outside (back button, notification link).
  useEffect(() => setSearchInput(q), [q]);

  useEffect(() => {
    if (searchInput.trim() === q) return;
    const timer = setTimeout(() => updateParams({ q: searchInput.trim() || null }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: stats } = useAdminBookingStats();
  const { data, isLoading, isError, isFetching } = useAdminBookings({
    page: page + 1,
    limit,
    statuses: tab.statuses?.join(','),
    attention: tab.attention,
    q: q || undefined,
    dateField,
    from: from || undefined,
    to: to || undefined,
    sortBy: tab.key === 'active' ? 'travelDate' : undefined,
  });

  const tabCount = (key: AdminBookingTabKey) => {
    if (!stats) return undefined;
    const definition = getTab(key);
    if (definition.attention) return stats.needsAttention;
    if (!definition.statuses) return stats.total;
    return definition.statuses.reduce((sum, status) => sum + (stats.byStatus[status] ?? 0), 0);
  };

  const hasFilters = Boolean(q || from || to);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <AdminPageHeader
        title="Bookings"
        subtitle="Every booking on the platform: payments, refunds, cancellations and disputes"
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Booked today"
            value={stats?.todayCreated ?? '—'}
            subtitle="Created since midnight IST"
            icon={<TodayIcon />}
            accent="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Active trips"
            value={
              stats
                ? (stats.byStatus[BookingStatus.CONFIRMED] ?? 0) +
                  (stats.byStatus[BookingStatus.UPCOMING] ?? 0) +
                  (stats.byStatus[BookingStatus.IN_PROGRESS] ?? 0)
                : '—'
            }
            subtitle="Confirmed, upcoming or in progress"
            icon={<EventNoteIcon />}
            accent="success.main"
            onClick={() => updateParams({ tab: 'active' })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Needs attention"
            value={stats?.needsAttention ?? '—'}
            subtitle="Disputes, stuck payments, overdue steps"
            icon={<ReportProblemIcon />}
            accent="warning.main"
            onClick={() => updateParams({ tab: 'attention' })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Booked value this month"
            value={stats ? (formatGuidePrice(stats.gmvThisMonth) ?? '—') : '—'}
            subtitle={stats ? `${stats.monthConfirmedCount} paid bookings` : undefined}
            icon={<CurrencyRupeeIcon />}
            accent="info.main"
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}
      >
        <Tabs
          value={tab.key}
          onChange={(_, value: AdminBookingTabKey) => updateParams({ tab: value === 'all' ? null : value })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1 }}
        >
          {ADMIN_BOOKING_TABS.map((definition) => {
            const count = tabCount(definition.key);
            return (
              <Tab
                key={definition.key}
                value={definition.key}
                label={count == null ? definition.label : `${definition.label} (${count})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            );
          })}
        </Tabs>

        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search booking id, traveler, guide, phone, email, destination"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ flex: '1 1 280px', minWidth: 0 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { maxLength: 100, 'aria-label': 'Search bookings' },
            }}
          />
          <TextField
            select
            size="small"
            label="Date"
            value={dateField}
            onChange={(event) =>
              updateParams({ dateField: event.target.value === 'travelDate' ? 'travelDate' : null })
            }
            sx={{ width: { xs: '100%', sm: 150 } }}
          >
            <MenuItem value="createdAt">Booked on</MenuItem>
            <MenuItem value="travelDate">Travel date</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(event) => updateParams({ from: event.target.value || null })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: 'calc(50% - 6px)', sm: 160 } }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(event) => updateParams({ to: event.target.value || null })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: 'calc(50% - 6px)', sm: 160 } }}
          />
          {hasFilters && (
            <Button
              size="small"
              onClick={() => {
                setSearchInput('');
                updateParams({ q: null, from: null, to: null });
              }}
            >
              Clear filters
            </Button>
          )}
        </Box>

        {isFetching && !isLoading && <LinearProgress sx={{ height: 2 }} />}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load bookings.
          </Alert>
        ) : (
          <>
            <AdminBookingsTable
              rows={data?.data ?? []}
              onSelect={(id) => updateParams({ id }, false)}
            />
            <TablePagination
              component="div"
              count={data?.pagination.total ?? 0}
              page={page}
              onPageChange={(_, next) => updateParams({ page: next ? String(next) : null }, false)}
              rowsPerPage={limit}
              onRowsPerPageChange={(event) =>
                updateParams({ limit: event.target.value === '20' ? null : event.target.value })
              }
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        )}
      </Paper>

      <AdminBookingDetailDrawer
        bookingId={selectedId}
        onClose={() => updateParams({ id: null }, false)}
      />
    </Box>
  );
}
